
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { OnboardingFormData } from "@/hooks/useOnboardingForm";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface PersonalInfoStepProps {
  formData: OnboardingFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRadioChange: (name: string, value: string) => void;
  handleSelectChange?: (name: string, value: string) => void;
}

// Generate feet options from 4 to 7
const feetOptions = Array.from({ length: 4 }, (_, i) => i + 4);

// Generate inch options from 0 to 11
const inchOptions = Array.from({ length: 12 }, (_, i) => i);

// Generate weight options from 80 to 350 lbs
const weightOptions = Array.from({ length: 271 }, (_, i) => i + 80);

export function PersonalInfoStep({
  formData,
  handleInputChange,
  handleRadioChange,
  handleSelectChange,
}: PersonalInfoStepProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          name="name"
          placeholder="Enter your full name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="you@example.com"
          value={formData.email}
          onChange={handleInputChange}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          placeholder="Create a secure password"
          value={formData.password || ""}
          onChange={handleInputChange}
          required
          minLength={6}
        />
        <p className="text-xs text-muted-foreground">
          Must be at least 6 characters long
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          placeholder="Confirm your password"
          value={formData.confirmPassword || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="age">Age</Label>
        <Input
          id="age"
          name="age"
          placeholder="Enter your age"
          value={formData.age}
          onChange={handleInputChange}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label>Gender</Label>
        <RadioGroup 
          value={formData.gender} 
          onValueChange={(value) => handleRadioChange("gender", value)}
          className="flex space-x-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="male" id="male" />
            <Label htmlFor="male">Male</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="female" id="female" />
            <Label htmlFor="female">Female</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="other" id="other" />
            <Label htmlFor="other">Other</Label>
          </div>
        </RadioGroup>
      </div>
      
      <div className="space-y-2">
        <Label>Height</Label>
        <div className="flex gap-2">
          {handleSelectChange && (
            <>
              <div className="w-1/2">
                <Select 
                  value={formData.heightFeet} 
                  onValueChange={(value) => handleSelectChange("heightFeet", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Feet" />
                  </SelectTrigger>
                  <SelectContent>
                    {feetOptions.map((foot) => (
                      <SelectItem key={foot} value={foot.toString()}>
                        {foot} ft
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-1/2">
                <Select 
                  value={formData.heightInches} 
                  onValueChange={(value) => handleSelectChange("heightInches", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Inches" />
                  </SelectTrigger>
                  <SelectContent>
                    {inchOptions.map((inch) => (
                      <SelectItem key={inch} value={inch.toString()}>
                        {inch} in
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Weight (lbs)</Label>
        {handleSelectChange && (
          <Select 
            value={formData.weight} 
            onValueChange={(value) => handleSelectChange("weight", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select weight" />
            </SelectTrigger>
            <SelectContent>
              {weightOptions.map((weight) => (
                <SelectItem key={weight} value={weight.toString()}>
                  {weight} lbs
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
